import { ContactMessage } from '../../domain/entities/ContactMessage.entity';
import { InvalidContactMessageError } from '../../domain/errors/InvalidContactMessageError';
import type { ContactMessageRepository } from '../../domain/repositories/ContactMessageRepository.interface';
import type { SubmitContactMessageDto } from '../dto/SubmitContactMessageDto';
import type { SubmitContactMessageResultDto } from '../dto/SubmitContactMessageResultDto';
import { ApplicationError, ValidationError, type EventBus, type Logger } from '@dma/shared';
import { generateRequestId } from '@dma/utils';

/**
 * Use Case per the pattern established by GetHomeHeroContentUseCase
 * (features/home/application/use-cases). Validates through the Domain layer,
 * persists via the injected Repository, and publishes ContactFormSubmitted
 * on the Event Bus for independent reactions (Audit Logging, future Admin
 * notification) — the use case itself does not know or care who listens.
 */
export class SubmitContactMessageUseCase {
  constructor(
    private readonly repository: ContactMessageRepository,
    private readonly eventBus: EventBus,
    private readonly logger: Logger
  ) {}

  public async execute(input: SubmitContactMessageDto): Promise<SubmitContactMessageResultDto> {
    const id = generateRequestId();
    const createdAt = new Date().toISOString();

    let message: ContactMessage;
    try {
      message = ContactMessage.create(input, id, createdAt);
    } catch (error) {
      if (error instanceof InvalidContactMessageError) {
        // Per docs/architecture/05-error-handling-strategy.md: user input
        // validation failures are ValidationError, not logged as system errors.
        throw new ValidationError(error.message);
      }
      throw error;
    }

    try {
      await this.repository.save(message);
    } catch (error) {
      this.logger.error('Failed to persist contact message', {
        messageId: message.id,
        cause: error instanceof Error ? error.message : String(error)
      });
      throw new ApplicationError(
        'CONTACT_MESSAGE_SAVE_FAILED',
        'ارسال پیام با مشکل مواجه شد. لطفاً دوباره تلاش کنید.',
        { cause: error }
      );
    }

    this.eventBus.publish('ContactFormSubmitted', {
      version: 1,
      messageId: message.id,
      submittedAt: message.createdAt
    });

    return { id: message.id, submittedAt: message.createdAt };
  }
}
